/**
 * Utility functions for error handling across the application
 */

// Error types for categorizing different error scenarios
export const ERROR_TYPES = {
  NETWORK: 'network',
  TIMEOUT: 'timeout',
  NOT_FOUND: 'not_found',
  SERVER: 'server',
  AUTH: 'auth',
  VALIDATION: 'validation',
  UNKNOWN: 'unknown'
};

// Error categories for user-facing messages
export const ERROR_CATEGORIES = {
  DATA_LOADING: 'data_loading',
  USER_INPUT: 'user_input',
  SYSTEM: 'system'
};

/**
 * Standard error messages that can be displayed to users
 */
export const ERROR_MESSAGES = {
  [ERROR_TYPES.NETWORK]: 'Network connection issue. Please check your internet connection.',
  [ERROR_TYPES.TIMEOUT]: 'Request timed out. Please try again later.',
  [ERROR_TYPES.NOT_FOUND]: 'The requested resource was not found.',
  [ERROR_TYPES.SERVER]: 'Server error occurred. Please try again later.',
  [ERROR_TYPES.AUTH]: 'Authentication error. Please log in again.',
  [ERROR_TYPES.VALIDATION]: 'Invalid input. Please check your entries.',
  [ERROR_TYPES.UNKNOWN]: 'An unexpected error occurred. Please try again.'
};

/**
 * Determine the type of error based on the error object
 * 
 * @param {Error} error - The error object to analyze
 * @returns {string} - The error type from ERROR_TYPES
 */
export const getErrorType = (error) => {
  if (!error) return ERROR_TYPES.UNKNOWN;
  
  // Network error detection
  if (!navigator.onLine || 
      error.message?.includes('network') || 
      error.name === 'NetworkError') {
    return ERROR_TYPES.NETWORK;
  }
  
  // Timeout detection
  if (error.name === 'AbortError' || 
      error.message?.includes('timeout') ||
      error.code === 'ETIMEDOUT') {
    return ERROR_TYPES.TIMEOUT;
  }
  
  // Not found detection
  if (error.status === 404 || 
      error.statusCode === 404 ||
      error.message?.includes('not found')) {
    return ERROR_TYPES.NOT_FOUND;
  }
  
  // Server error detection
  if ((error.status >= 500 && error.status < 600) || 
      (error.statusCode >= 500 && error.statusCode < 600) ||
      error.message?.includes('server error')) {
    return ERROR_TYPES.SERVER;
  }
  
  // Auth error detection
  if (error.status === 401 || 
      error.statusCode === 401 ||
      error.status === 403 || 
      error.statusCode === 403 ||
      error.message?.includes('unauthorized') ||
      error.message?.includes('forbidden')) {
    return ERROR_TYPES.AUTH;
  }
  
  // Validation error detection
  if (error.name === 'ValidationError' ||
      error.message?.includes('validation') ||
      (error.status === 400 || error.statusCode === 400)) {
    return ERROR_TYPES.VALIDATION;
  }
  
  return ERROR_TYPES.UNKNOWN;
};

/**
 * Get a user-friendly error message based on error type
 * 
 * @param {Error|string} error - Error object or error type
 * @param {string} customMessage - Optional custom message to use instead
 * @returns {string} - User-friendly error message
 */
export const getUserFriendlyMessage = (error, customMessage = '') => {
  if (!error) return ERROR_MESSAGES[ERROR_TYPES.UNKNOWN];
  
  // If custom message provided, use it
  if (customMessage) return customMessage;
  
  // If error is already a string, check if it's an error type or use as is
  if (typeof error === 'string') {
    return ERROR_MESSAGES[error] || error;
  }
  
  // Determine error type and get corresponding message
  const errorType = getErrorType(error);
  return ERROR_MESSAGES[errorType];
};

/**
 * Log error with standardized format
 * 
 * @param {Error} error - The error object
 * @param {string} context - Where the error occurred
 * @param {Object} additionalInfo - Any additional useful information
 */
export const logError = (error, context = '', additionalInfo = {}) => {
  const errorType = getErrorType(error);
  const timestamp = new Date().toISOString();
  
  const logData = {
    timestamp,
    type: errorType,
    message: error.message || 'No error message',
    context,
    stack: error.stack,
    ...additionalInfo
  };
  
  // In production you might want to send this to a logging service
  console.error('[ERROR LOG]', logData);
  
  // Return the log data in case it's needed
  return logData;
};

/**
 * Create a standardized error object
 * 
 * @param {string} message - Error message
 * @param {string} type - Error type from ERROR_TYPES
 * @param {Object} additionalData - Any additional error data
 * @returns {Error} - Enhanced error object
 */
export const createError = (message, type = ERROR_TYPES.UNKNOWN, additionalData = {}) => {
  const error = new Error(message);
  error.type = type;
  error.timestamp = new Date().toISOString();
  
  // Add any additional data
  Object.keys(additionalData).forEach(key => {
    error[key] = additionalData[key];
  });
  
  return error;
};

/**
 * Safely handle async operations with standardized error handling
 * 
 * @param {Function} asyncFn - Async function to execute
 * @param {Function} onSuccess - Success handler
 * @param {Function} onError - Error handler
 * @param {string} context - Context for error logging
 */
export const safeAsync = async (asyncFn, onSuccess, onError, context = '') => {
  try {
    const result = await asyncFn();
    if (onSuccess && typeof onSuccess === 'function') {
      onSuccess(result);
    }
    return result;
  } catch (error) {
    // Log the error
    logError(error, context);
    
    // Handle the error
    if (onError && typeof onError === 'function') {
      onError(error);
    }
    
    return null;
  }
}; 